/**
 * Normalizing function that maps the location data in JSON format to the google places format.
 */
function normalizePlacesData(rawData: any[]): google.maps.places.PlaceResult[] {
  return rawData.map((place) => ({
    address_components: place.address_components || undefined,
    adr_address: place.adr_address || undefined,
    aspects: place.aspects || undefined,
    business_status: place.business_status || undefined,
    formatted_address: place.formatted_address || undefined,
    formatted_phone_number: place.formatted_phone_number || undefined,
    geometry: place.geometry
      ? {
          location:
            place.geometry.location &&
            new google.maps.LatLng(
              place.geometry.location.lat,
              place.geometry.location.lng
            ),
          viewport: place.geometry.viewport
            ? new google.maps.LatLngBounds(
                new google.maps.LatLng(
                  place.geometry.viewport.southwest.lat,
                  place.geometry.viewport.southwest.lng
                ),
                new google.maps.LatLng(
                  place.geometry.viewport.northeast.lat,
                  place.geometry.viewport.northeast.lng
                )
              )
            : undefined,
        }
      : undefined,
    html_attributions: place.html_attributions || undefined,
    icon: place.icon || undefined,
    icon_background_color: place.icon_background_color || undefined,
    icon_mask_base_uri: place.icon_mask_base_uri || undefined,
    international_phone_number: place.international_phone_number || undefined,
    name: place.name || undefined,
    opening_hours: place.opening_hours || undefined,
    permanently_closed: place.permanently_closed || undefined,
    photos: undefined, // currently not supported
    place_id: place.place_id || undefined,
    plus_code: place.plus_code || undefined,
    price_level: place.price_level || undefined,
    rating: place.rating || undefined,
    reviews: place.reviews || undefined,
    types: place.types || undefined,
    url: place.url || undefined,
    user_ratings_total: place.user_ratings_total || undefined,
    utc_offset: place.utc_offset || undefined,
    utc_offset_minutes: place.utc_offset_minutes || undefined,
    vicinity: place.vicinity || undefined,
    website: place.website || undefined,
  }));
}

/**
 * Filters out privately stored google places locations from local json file of google places.
 * @param userLocation - The user's location
 * @param privateLocations - The locations to filter
 * @param searchString - The search string to filter by
 * @param maxDistance - The maximum distance to allow
 * @returns The filtered locations
 */
export async function filterPrivateLocations(
  userLocation: google.maps.LatLngLiteral,
  searchString: string,
  maxDistance = 50000
): Promise<google.maps.places.PlaceResult[]> {
  // Gcreate a get request to fetch the data from the aws s3 bucket
  const privateLocations = await getLocationData();

  const places = normalizePlacesData(privateLocations);

  return places.filter((location) => {
    if (!location.geometry || !location.geometry.location) return false;

    const lat = location.geometry.location.lat();
    const lng = location.geometry.location.lng();
    const distance = haversineDistance(
      userLocation.lat,
      userLocation.lng,
      lat,
      lng
    );

    // Check if the location has a valid type
    const hasValidType = location.types?.some((type: string) =>
      searchString.includes(type)
    );

    return distance <= maxDistance && hasValidType;
  });
}

// Haversine formula to calculate distance between two lat/lng points
function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  const R = 6371000; // Radius of Earth in meters
  const toRadians = (degrees: number) => degrees * (Math.PI / 180);

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Opens or creates an IndexedDB database and object store for location data.
 */
function openIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('LocationDataDB', 1);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('locations')) {
        db.createObjectStore('locations', {
          keyPath: 'id',
          autoIncrement: true,
        });
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      reject(new Error('Failed to open IndexedDB'));
    };
  });
}

/**
 * Saves location data to IndexedDB.
 * @param data - The data to save.
 */
async function saveDataToIndexedDB(data: any[]): Promise<void> {
  const db = await openIndexedDB();
  const transaction = db.transaction('locations', 'readwrite');
  const store = transaction.objectStore('locations');

  // Clear existing data
  store.clear();

  // Add new data
  data.forEach((item) => {
    store.add(item);
  });

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      console.log('Data saved to IndexedDB successfully.');
      resolve();
    };

    transaction.onerror = (event) => {
      console.error('Error saving data to IndexedDB:', event);
      reject(new Error('Failed to save data to IndexedDB'));
    };
  });
}

/**
 * Retrieves location data from IndexedDB.
 * @returns The location data.
 */
async function getDataFromIndexedDB(): Promise<any[]> {
  const db = await openIndexedDB();
  const transaction = db.transaction('locations', 'readonly');
  const store = transaction.objectStore('locations');

  return new Promise((resolve, reject) => {
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = (event) => {
      console.error('Error retrieving data from IndexedDB:', event);
      reject(new Error('Failed to retrieve data from IndexedDB'));
    };
  });
}

/**
 * Fetches location data, either from IndexedDB or from the server.
 * @returns The location data.
 */
async function getLocationData(): Promise<any[]> {
  try {
    // Check if data exists in IndexedDB
    const indexedDBData = await getDataFromIndexedDB();
    if (indexedDBData.length > 0) {
      return indexedDBData;
    } else {
      // Fetch the data from the server
      const response = await fetch(
        'https://rokketmed-static-assets.s3.us-east-1.amazonaws.com/merged.json'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch location data');
      }

      const data = await response.json();

      // Save the data to IndexedDB
      await saveDataToIndexedDB(data);

      return data;
    }
  } catch (error) {
    console.error(error);
    return [];
  }
}
